import { useListApiKeys } from "@package/api-client/hooks/APIKeysController";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, KeyRound, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateApiKey, useDeleteApiKey, useRevokeApiKey } from "@/api/apps";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/app/$appId/api-keys/")({
	component: ApiKeysPage,
});

function ApiKeysPage() {
	const { t } = useTranslation();
	const { orgId, appId } = Route.useParams();

	const { data: apiKeys, isLoading: keysLoading } = useListApiKeys(orgId, appId);

	const createApiKey = useCreateApiKey(orgId, appId);
	const revokeApiKey = useRevokeApiKey(orgId, appId);
	const deleteApiKey = useDeleteApiKey(orgId, appId);

	const [showCreateKeyForm, setShowCreateKeyForm] = useState(false);
	const [newKeyName, setNewKeyName] = useState("");
	const [createdKey, setCreatedKey] = useState<{ key: string; keyPrefix: string } | null>(null);
	const [copied, setCopied] = useState(false);
	const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null);
	const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null);

	const handleCreateKey = (e: React.FormEvent) => {
		e.preventDefault();
		if (!newKeyName.trim()) return;
		createApiKey.mutate(
			{ orgId, appId, data: { name: newKeyName.trim() } },
			{
				onSuccess: (data: { key: string; keyPrefix: string }) => {
					setNewKeyName("");
					setShowCreateKeyForm(false);
					setCreatedKey({ key: data.key, keyPrefix: data.keyPrefix });
				},
			},
		);
	};

	const handleCopyKey = async () => {
		if (!createdKey) return;
		await navigator.clipboard.writeText(createdKey.key);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const handleRevokeKey = (keyId: string) => {
		revokeApiKey.mutate({ orgId, appId, keyId }, { onSuccess: () => setRevokeKeyId(null) });
	};

	const handleDeleteKey = (keyId: string) => {
		deleteApiKey.mutate({ orgId, appId, keyId }, { onSuccess: () => setDeleteKeyId(null) });
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<h1 data-testid="api-keys-title" className="text-2xl font-bold">
					{t("apiKeys.title")}
				</h1>
				<button
					type="button"
					data-testid="api-keys-create-btn"
					className="btn btn-primary btn-sm"
					onClick={() => setShowCreateKeyForm(true)}
				>
					<Plus className="w-4 h-4" />
					{t("apiKeys.createNew")}
				</button>
			</div>

			{/* API Keys Table */}
			<div className="card bg-base-100 border border-base-300 shadow-sm">
				<div className="card-body">
					{keysLoading && (
						<div className="flex justify-center py-8">
							<span className="loading loading-spinner loading-md" />
						</div>
					)}

					{!keysLoading && (!apiKeys || apiKeys.length === 0) && (
						<div className="text-center py-8 text-base-content/50">
							<KeyRound className="w-8 h-8 mx-auto mb-2 opacity-30" />
							<p>{t("common.noResults")}</p>
						</div>
					)}

					{!keysLoading && apiKeys && apiKeys.length > 0 && (
						<div className="overflow-x-auto">
							<table data-testid="api-keys-table" className="table">
								<thead>
									<tr>
										<th>{t("apiKeys.name")}</th>
										<th>{t("apiKeys.prefix")}</th>
										<th>Status</th>
										<th>{t("apiKeys.lastUsed")}</th>
										<th>{t("messages.createdAt")}</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{apiKeys.map((key) => (
										<tr key={key.id}>
											<td className="font-medium">{key.name}</td>
											<td>
												<code className="text-sm bg-base-200 px-2 py-0.5 rounded">
													{key.keyPrefix}...
												</code>
											</td>
											<td>
												<span
													className={`badge badge-sm ${key.isActive ? "badge-success" : "badge-ghost"}`}
												>
													{key.isActive ? t("apiKeys.active") : t("apiKeys.inactive")}
												</span>
											</td>
											<td className="text-sm text-base-content/60">
												{key.lastUsedAt
													? new Date(key.lastUsedAt).toLocaleDateString()
													: t("apiKeys.never")}
											</td>
											<td className="text-sm text-base-content/60">
												{new Date(key.createdAt).toLocaleDateString()}
											</td>
											<td>
												<div className="flex gap-1 justify-end">
													{key.isActive && (
														<button
															type="button"
															className="btn btn-ghost btn-xs"
															onClick={() => setRevokeKeyId(key.id)}
														>
															{t("apiKeys.revoke")}
														</button>
													)}
													<button
														type="button"
														className="btn btn-ghost btn-xs text-error"
														onClick={() => setDeleteKeyId(key.id)}
													>
														<Trash2 className="w-3 h-3" />
													</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>

			{/* Create API Key Modal */}
			{showCreateKeyForm && (
				<dialog className="modal modal-open">
					<div className="modal-box">
						<h3 className="font-bold text-lg">{t("apiKeys.createNew")}</h3>
						<form onSubmit={handleCreateKey} className="mt-4">
							<div className="form-control">
								<label className="label" htmlFor="key-name-input">
									<span className="label-text">{t("apiKeys.name")}</span>
								</label>
								<input
									id="key-name-input"
									type="text"
									data-testid="key-name-input"
									className="input input-bordered w-full"
									placeholder={t("apiKeys.name")}
									value={newKeyName}
									onChange={(e) => setNewKeyName(e.target.value)}
									maxLength={200}
									autoFocus
								/>
							</div>
							<div className="modal-action">
								<button
									type="button"
									className="btn"
									onClick={() => {
										setShowCreateKeyForm(false);
										setNewKeyName("");
									}}
								>
									{t("common.cancel")}
								</button>
								<button
									type="submit"
									className="btn btn-primary"
									disabled={!newKeyName.trim() || createApiKey.isPending}
								>
									{createApiKey.isPending ? t("common.creating") : t("common.create")}
								</button>
							</div>
						</form>
					</div>
					<form method="dialog" className="modal-backdrop">
						<button
							type="button"
							onClick={() => {
								setShowCreateKeyForm(false);
								setNewKeyName("");
							}}
						>
							close
						</button>
					</form>
				</dialog>
			)}

			{/* Created Key Display Modal */}
			{createdKey && (
				<dialog className="modal modal-open">
					<div className="modal-box">
						<h3 className="font-bold text-lg">{t("apiKeys.key")}</h3>
						<div className="alert alert-warning mt-4">
							<p className="text-sm">{t("apiKeys.showOnce")}</p>
						</div>
						<div className="mt-4 flex items-center gap-2">
							<code className="flex-1 bg-base-200 p-3 rounded-lg text-sm break-all select-all">
								{createdKey.key}
							</code>
							<button type="button" className="btn btn-sm btn-square" onClick={handleCopyKey}>
								{copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
							</button>
						</div>
						<div className="modal-action">
							<button type="button" className="btn btn-primary" onClick={() => setCreatedKey(null)}>
								{t("common.confirm")}
							</button>
						</div>
					</div>
				</dialog>
			)}

			{/* Revoke Key Confirmation */}
			{revokeKeyId && (
				<dialog className="modal modal-open">
					<div className="modal-box">
						<h3 className="font-bold text-lg">{t("common.confirm")}</h3>
						<p className="py-4">{t("apiKeys.revokeConfirm")}</p>
						<div className="modal-action">
							<button type="button" className="btn" onClick={() => setRevokeKeyId(null)}>
								{t("common.cancel")}
							</button>
							<button
								type="button"
								className="btn btn-warning"
								disabled={revokeApiKey.isPending}
								onClick={() => handleRevokeKey(revokeKeyId)}
							>
								{revokeApiKey.isPending ? t("common.updating") : t("apiKeys.revoke")}
							</button>
						</div>
					</div>
					<form method="dialog" className="modal-backdrop">
						<button type="button" onClick={() => setRevokeKeyId(null)}>
							close
						</button>
					</form>
				</dialog>
			)}

			{/* Delete Key Confirmation */}
			{deleteKeyId && (
				<dialog className="modal modal-open">
					<div className="modal-box">
						<h3 className="font-bold text-lg">{t("common.confirm")}</h3>
						<p className="py-4">{t("apiKeys.deleteConfirm")}</p>
						<div className="modal-action">
							<button type="button" className="btn" onClick={() => setDeleteKeyId(null)}>
								{t("common.cancel")}
							</button>
							<button
								type="button"
								className="btn btn-error"
								disabled={deleteApiKey.isPending}
								onClick={() => handleDeleteKey(deleteKeyId)}
							>
								{deleteApiKey.isPending ? t("common.deleting") : t("common.delete")}
							</button>
						</div>
					</div>
					<form method="dialog" className="modal-backdrop">
						<button type="button" onClick={() => setDeleteKeyId(null)}>
							close
						</button>
					</form>
				</dialog>
			)}
		</div>
	);
}
